import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getFeatureFlagFromParams,
  isFeatureFlagEnabled,
  setFeatureFlagCookie,
} from "@/react/utils/featureFlagCookies";
import { useCurrentCommunityContext } from "@circle-react/apps/AuthenticationApp";
import type { ButtonVariant } from "@circle-react-uikit/Button";
import { NEW_ONBOARDING_TOTAL_STEPS, TOTAL_STEPS } from "../constants";

const COOKIE_NAME = "new_onboarding";

/**
 * Hook to determine eligibility for new onboarding flow.
 * Precedence: 1) URL param, 2) cookie, 3) server (context)
 */
export const useIsNewOnboardingFlow = (): {
  isNewOnboardingFlow: boolean;
  onboardingButtonVariant: ButtonVariant;
  totalSteps: number;
  isLoading: boolean;
} => {
  const { isEligibleForNewOnboardingFlow } =
    useCurrentCommunityContext?.() || {};
  const [isNewOnboardingFlow, setIsNewOnboardingFlow] = useState(
    Boolean(isEligibleForNewOnboardingFlow),
  );
  const [isLoading, setIsLoading] = useState(true);
  const { search } = useLocation();

  useEffect(() => {
    const paramValue = getFeatureFlagFromParams(search, "new_onboarding");

    // 1. Highest precedence: URL param (overrides everything, sets cookie)
    if (paramValue !== null) {
      setFeatureFlagCookie(COOKIE_NAME, paramValue);
      setIsNewOnboardingFlow(paramValue === "true");
      setIsLoading(false);
    }
    // 2. Next: Cookie (if present and true, overrides server value)
    else if (isFeatureFlagEnabled(COOKIE_NAME)) {
      setIsNewOnboardingFlow(true);
      setIsLoading(false);
    }
    // 3. Fallback: Server value (from context)
    else {
      setIsNewOnboardingFlow(Boolean(isEligibleForNewOnboardingFlow));
      setIsLoading(false);
    }
  }, [search, isEligibleForNewOnboardingFlow]);

  const onboardingButtonVariant: ButtonVariant = isNewOnboardingFlow
    ? "marketing-gradient-blue"
    : "marketing-gradient";

  const totalSteps = isNewOnboardingFlow
    ? NEW_ONBOARDING_TOTAL_STEPS
    : TOTAL_STEPS;

  return {
    isNewOnboardingFlow,
    onboardingButtonVariant,
    totalSteps,
    isLoading,
  };
};
